/** Dashboard (Access Group) permissions view for staff/guests.
 *  Shows cached vs DB-effective permissions + AG membership, with cache /
 *  staff action buttons. Read pattern mirrors dashboard UserProfilePermissions. */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as usersApi from '@/api/users';
import type { ConsoleUserDetail, PermissionDict } from '@/api/users';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import { act, CopyableId, ValueActions } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';

import {
  accessGroupActions,
  managedGroupsActions,
  permissionTagActions,
  rbacOperatorActions,
  type UserCtx,
} from '../userLinks';

interface Props {
  user: ConsoleUserDetail;
  onChanged: () => void;
}

export function DashboardPermissionsTab({ user, onChanged }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_perm_grant');
  const [confirm, setConfirm] = useState<'clear' | 'staff' | null>(null);
  const ctx: UserCtx = { tenantId: user.tour_operator?.id ?? null };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboardPerms', user.id],
    queryFn: () => usersApi.dashboardPermissions(user.id),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['dashboardPerms', user.id] });
    qc.invalidateQueries({ queryKey: ['consoleUsers'] });
    onChanged();
  };

  const refresh = useMutation({
    mutationFn: () => usersApi.refreshPermissionCache(user.id),
    onSuccess: () => {
      snackbar.show('Permission cache refreshed.', 'success');
      invalidate();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Refresh failed.', 'error'),
  });
  const clear = useMutation({
    mutationFn: () => usersApi.clearPermissionCache(user.id),
    onSuccess: () => {
      snackbar.show('Permission cache cleared.', 'success');
      invalidate();
    },
  });
  const revokeStaff = useMutation({
    mutationFn: () => usersApi.revokeStaff(user.id),
    onSuccess: (r) => {
      snackbar.show(`Staff status revoked (removed ${r.tou_removed} role link(s)).`, 'success');
      invalidate();
    },
  });

  if (isLoading) return <CircularProgress />;
  if (isError || !data) return <Alert severity="error">Couldn’t load dashboard permissions.</Alert>;

  const sharedGuest = data.is_guest && data.guest_group?.shared === true;

  return (
    <Stack spacing={3}>
      {data.is_guest && data.guest_group && (
        <Alert severity="info">
          This is a <strong>guest</strong>. Effective permissions come from the tenant&apos;s
          shared guest access group{' '}
          <ValueActions actions={accessGroupActions(data.guest_group, ctx.tenantId)}>
            <strong>{data.guest_group.name}</strong>
          </ValueActions>
          {sharedGuest
            ? ' — the cache below is shared by every guest of this tenant.'
            : ' (per-user in franchise mode).'}
        </Alert>
      )}
      {/* Flags + TOU */}
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>Account flags</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <FlagChip label="Active" on={data.flags.is_active} />
          <FlagChip label="Staff" on={data.flags.is_staff} />
          <FlagChip label="Superuser" on={data.flags.is_superuser} />
          <FlagChip label="Employee" on={data.flags.is_employee} />
          {data.flags.is_deleted && <Chip size="small" color="error" label="Deleted" />}
        </Stack>
      </Box>

      {data.tour_operator_users.length > 0 && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Tour_Operator_User records (legacy roles)
          </Typography>
          <Stack spacing={0.5}>
            {data.tour_operator_users.map((tou) => (
              <Box key={tou.id}>
                <ValueActions actions={rbacOperatorActions(tou.tour_operator)}>
                  <strong>{tou.tour_operator.name}</strong> ({tou.tour_operator.code})
                </ValueActions>
                {': '}
                {tou.roles.length ? (
                  tou.roles.map((r) => <Chip key={r.id} size="small" label={r.name} sx={{ mr: 0.5 }} />)
                ) : (
                  <em>no roles</em>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Access groups */}
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>Access groups</Typography>
        {data.agroups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No access-group membership.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {data.agroups.map((ag) => (
              <Box key={ag.id}>
                <ValueActions actions={accessGroupActions(ag, ag.tour_operator.id)}>
                  {/* Inline link stays same-tab; the popover adds the filters.
                      MUI Link, so it is the theme's navy, not browser blue. */}
                  <Link component={RouterLink} to={`/rbac/access-groups/${ag.id}`}>
                    {ag.name}
                  </Link>
                </ValueActions>
                {' — '}
                <ValueActions actions={rbacOperatorActions(ag.tour_operator)}>
                  {ag.tour_operator.name}
                </ValueActions>
                {ag.full_access && <FullAccessChip sx={{ ml: 0.5 }} />}
                {ag.managed && (
                  <ValueActions chip actions={managedGroupsActions()} sx={{ ml: 0.5 }}>
                    <Chip size="small" label="managed" />
                  </ValueActions>
                )}
                {ag.licensee && (
                  <ValueActions
                    chip
                    actions={rbacOperatorActions(ag.licensee)}
                    sx={{ ml: 0.5 }}
                  >
                    <Chip size="small" variant="outlined" label={`licensee: ${ag.licensee.name}`} />
                  </ValueActions>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Cache vs DB */}
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>Effective permissions</Typography>
        {data.cache_key && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {/* The Redis key behind the "Cached" column — the one thing that
                makes a cache-empty incident reproducible outside the console. */}
            Cache key: <CopyableId value={data.cache_key} label="Copy cache key" />
          </Typography>
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <PermsColumn
            title="Cached (Redis)"
            perms={data.cache}
            emptyNote="Cache is empty or missing. This diagnostics view does NOT self-heal; use Refresh to rebuild."
            tagmap={data.tagmap}
          />
          <Divider orientation="vertical" flexItem />
          <PermsColumn
            title="Effective (DB)"
            perms={data.effective}
            emptyNote="No permissions computed from the database."
            tagmap={data.tagmap}
          />
        </Stack>
      </Box>

      {/* Actions */}
      {canManage && (
        <>
          <Divider />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="outlined" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
              Refresh cache
            </Button>
            {/* Neutral, not amber (amber means unsaved/pending): the confirm
                dialog carries the warning, and Refresh undoes it. */}
            <Button variant="outlined" onClick={() => setConfirm('clear')}>
              Clear cache
            </Button>
            {data.flags.is_staff && (
              <Button color="error" variant="outlined" onClick={() => setConfirm('staff')}>
                Revoke staff status
              </Button>
            )}
          </Stack>
        </>
      )}

      {confirm === 'clear' && (
        <ConfirmDialog
          open
          title="Clear permission cache?"
          message={
            sharedGuest
              ? 'This user is a guest — the cache is the SHARED guest-group key, so clearing it drops cached permissions for EVERY guest of this tenant until the next refresh. This action is audit-logged.'
              : 'Strips the user of all cached permissions until the next refresh — they will lose dashboard access in the meantime. This action is audit-logged.'
          }
          confirmLabel="Clear cache"
          destructive
          onConfirm={() => {
            clear.mutate();
            setConfirm(null);
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === 'staff' && (
        <ConfirmDialog
          open
          title="Revoke staff status?"
          message="Sets is_staff=false and removes all Tour_Operator_User role links. The user keeps their account but loses dashboard/staff privileges."
          confirmLabel="Revoke staff"
          destructive
          onConfirm={() => {
            revokeStaff.mutate();
            setConfirm(null);
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </Stack>
  );
}

/** An account flag in icon + words ("Active" / "Not staff"), neutral — the
 *  state never rides on a green fill alone. */
function FlagChip({ label, on }: { label: string; on: boolean }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={on ? <CheckIcon /> : <CloseIcon />}
      label={on ? label : `Not ${label.toLowerCase()}`}
      sx={on ? undefined : { color: 'text.secondary' }}
    />
  );
}

/** "Full access" is a fact about a group, not a pending change: neutral outlined
 *  with an icon, never Pending Amber. */
function FullAccessChip({ sx }: { sx?: SxProps<Theme> }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<LockOpenOutlinedIcon />}
      label="Full access"
      {...(sx ? { sx } : {})}
    />
  );
}

function PermsColumn({
  title,
  perms,
  emptyNote,
  tagmap,
}: {
  title: string;
  perms: PermissionDict | null;
  emptyNote: string;
  tagmap: Record<string, { id: number; name: string }>;
}) {
  const rows: { scope: string; action: string; tagIds: number[] }[] = [];
  if (perms) {
    for (const [scope, actions] of Object.entries(perms.perms)) {
      for (const [action, tagIds] of Object.entries(actions)) {
        rows.push({ scope, action, tagIds });
      }
    }
  }
  return (
    <Box sx={{ flex: 1, minWidth: 280 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      {perms?.full_access && <FullAccessChip sx={{ mb: 1 }} />}
      {perms === null ? (
        <Alert severity="warning">{emptyNote}</Alert>
      ) : rows.length === 0 && !perms.full_access ? (
        <Typography variant="body2" color="text.secondary">No scoped permissions.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Scope</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Tags</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={`${r.scope}.${r.action}.${i}`}>
                <TableCell>{r.scope}</TableCell>
                <TableCell>{r.action}</TableCell>
                <TableCell>
                  {r.tagIds.length === 0
                    ? '—'
                    : r.tagIds.map((id, k) => {
                        const tag = tagmap[String(id)];
                        return (
                          <Box component="span" key={id}>
                            {k > 0 && ', '}
                            {tag ? (
                              <ValueActions actions={permissionTagActions(tag)}>
                                {tag.name}
                              </ValueActions>
                            ) : (
                              // The tagmap is the only name source; an id it
                              // does not cover still opens its tag page.
                              <ValueActions actions={[act.open(`/tags/${id}`, 'Open tag')]}>
                                {`#${id}`}
                              </ValueActions>
                            )}
                          </Box>
                        );
                      })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
